import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReviewQueueClosed, ReviewQueueItem } from "@resume-cloudflare/shared";
import { api, ApiError } from "./api";
import { useMutationToast } from "../hooks/useMutationToast";

// Approval gate (docs/SUBMISSION_FLOW_SPRINTS.md PR B). The /review queue
// lists packets in preparing/approved; approving freezes the exact packet by
// its snapshot hash, so a packet that changed while you were reading it is
// rejected (409 stale_snapshot) and re-rendered rather than approved blind.

export interface ReviewError {
  status: number;
  error: string;
  body: Record<string, unknown>;
}

function toReviewError(err: unknown): ReviewError {
  if (err instanceof ApiError) {
    let body: Record<string, unknown> = {};
    try {
      body = JSON.parse(err.message);
    } catch {
      // not JSON
    }
    return {
      status: err.status,
      error: typeof body.error === "string" ? body.error : err.message,
      body,
    };
  }
  return { status: 0, error: "network_error", body: {} };
}

export function useReviewQueue() {
  return useQuery({
    queryKey: ["review-queue"],
    queryFn: () =>
      api<{ items: ReviewQueueItem[]; live_total?: number; closed?: ReviewQueueClosed[] }>(
        "/api/apply/review-queue",
      ),
    staleTime: 10_000,
  });
}

function useInvalidateReview(applicationId: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["review-queue"] });
    qc.invalidateQueries({ queryKey: ["apply-kit", applicationId] });
    qc.invalidateQueries({ queryKey: ["approval-diff", applicationId] });
    qc.invalidateQueries({ queryKey: ["applications"] });
    qc.invalidateQueries({ queryKey: ["hunt-agenda"] });
  };
}

export function useApprovePacket(applicationId: string) {
  const invalidate = useInvalidateReview(applicationId);
  return useMutationToast<{ approval: { id: string } }, ReviewError, { snapshot_sha256: string }>({
    mutationFn: async (input) => {
      try {
        return await api(`/api/apply/${applicationId}/approve`, {
          method: "POST",
          body: JSON.stringify(input),
        });
      } catch (err) {
        throw toReviewError(err);
      }
    },
    onSettled: invalidate,
    success: "Approved. This exact packet is what gets submitted.",
    errorPrefix: "Not approved",
  });
}

export function useRevokeApproval(applicationId: string) {
  const invalidate = useInvalidateReview(applicationId);
  return useMutationToast<{ revoked: boolean }, ReviewError, void>({
    mutationFn: async () => {
      try {
        return await api(`/api/apply/${applicationId}/approval`, { method: "DELETE" });
      } catch (err) {
        throw toReviewError(err);
      }
    },
    onSettled: invalidate,
    success: "Approval withdrawn — back in review.",
    errorPrefix: "Couldn't revoke",
  });
}

export interface ApprovalDiff {
  fields: Array<{ name: string; label: string; approved: string | null; current: string | null }>;
  documents_changed: boolean;
}

// What changed since approval. The route answers 409 stale_approval with the
// diff in the body — that 409 IS the payload here, not a failure.
export function useApprovalDiff(applicationId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["approval-diff", applicationId],
    enabled,
    queryFn: async (): Promise<ApprovalDiff | null> => {
      const res = await fetch(`/api/apply/${applicationId}/approved-snapshot`, {
        credentials: "same-origin",
      });
      if (res.status !== 409) return null;
      const body = (await res.json().catch(() => ({}))) as { changes?: ApprovalDiff };
      return body.changes ?? null;
    },
  });
}

// Set (or override) one field's value for THIS application only — writes an
// application answer row, which wins over profile/bank prefills. Any edit
// changes the snapshot hash, so a live approval goes stale by design.
export function useSetFieldValue(applicationId: string) {
  const invalidate = useInvalidateReview(applicationId);
  return useMutationToast<unknown, ReviewError, { field_name: string; answer: string }>({
    mutationFn: async (input) => {
      try {
        return await api(`/api/apply/${applicationId}/answers/set`, {
          method: "POST",
          body: JSON.stringify(input),
        });
      } catch (err) {
        throw toReviewError(err);
      }
    },
    onSettled: invalidate,
    success: "Saved.",
    errorPrefix: "Couldn't save",
  });
}
